import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { edgeErrorMessage } from "@/lib/edge-errors";
import type { DBCustomerCompanyInfo } from "@/lib/db-types";

// Rå Brønnøysund-svar (raw_*) er store og hentes aldri til UI.
const COLUMNS =
  "customer_id, organization_number, legal_name, organization_form, industry_code, industry_description, " +
  "employees, founded_on, website, municipality, business_address, is_bankrupt, is_winding_up, roles, " +
  "fiscal_year, revenue_nok, operating_result_nok, net_result_nok, equity_nok, " +
  "previous_fiscal_year, previous_revenue_nok, fetched_at, last_error";

/** Bedriftsinfo for én kunde, eller null når den ikke er hentet ennå. Bundet av customer_id. */
export function useCustomerCompanyInfo(customerId: string | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["customer-company-info", customerId],
    queryFn: async (): Promise<DBCustomerCompanyInfo | null> => {
      const { data, error } = await supabase
        .from("customer_company_info")
        .select(COLUMNS)
        .eq("customer_id", customerId!)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as DBCustomerCompanyInfo | null) ?? null;
    },
    enabled: !!customerId && (options.enabled ?? true),
    staleTime: 5 * 60 * 1000,
  });
}

interface SyncResponse {
  customers?: number;
  updated?: number;
  signals?: number;
  errors?: number;
  skipped?: string;
  error?: string;
}

/** «Oppdater nå»: henter ny bedriftsinfo for én kunde via edge function (admin). */
export function useRefreshCompanyInfo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (customerId: string) => {
      const { data, error } = await supabase.functions.invoke<SyncResponse>("brreg-sync-customers", {
        body: { customer_id: customerId },
      });
      if (error) throw new Error(await edgeErrorMessage(error, data, "Kunne ikke hente fra Brønnøysund"));
      if (data?.skipped === "flag_off") throw new Error("Signaler er ikke slått på ennå");
      if (data?.error) throw new Error(data.error);
      // errors > 0 med én kunde: raden har last_error, som seksjonen viser.
      if (data?.customers === 0) throw new Error("Kunden er ikke et selskap med gyldig organisasjonsnummer");
      if (data?.errors) throw new Error("Brønnøysund svarte ikke som forventet. Prøv igjen senere.");
      return data;
    },
    // onSettled: logo-steget (VIB-2892) kan ha lagret logo_url selv når Brønnøysund feilet.
    onSettled: async (_data, _error, customerId) => {
      await invalidateFor(qc, "customer_company_info", { customerId });
      await invalidateFor(qc, "customers", { customerId });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
